import { ERROR_CODES } from "@closer/shared";

import { ApiRequestError } from "./apiClient";

/**
 * Sunucu hatasını GÜVENLİ bir ekran durumuna çeviren tek kapı (CLOS-106 `[067]`).
 *
 * ### Neden tek kapı
 *
 * Her ekran kendi `catch` bloğunda kendi metnini yazsaydı, biri gün gelir `error.message`
 * içindeki bağlantı dizesini ekrana basardı. Kartın ilk kriteri: *"hata mesajı stack
 * trace, altyapı adı, gizli kimlik, permission ayrıntısı veya hassas teknik bilgi
 * sızdırmaz."*
 *
 * ### ⚠ Bu dosya İŞ KURALI ÜRETMEZ
 *
 * Kartın altıncı kriteri: *"lisans ve limit durumları ... merkezî motorun makine
 * tarafından okunabilir neden kodunu güvenli kullanıcı mesajına çevirir; BAĞIMSIZ İŞ
 * KURALI ÜRETMEZ."* Burada hiçbir yetki, kapasite ya da lisans HESABI yok — yalnız
 * sunucunun verdiği kodun sunumu.
 */

export type UiStateKind =
  | "offline"
  | "unauthorized"
  | "twoFactorRequired"
  | "forbiddenOrMissing"
  | "forbidden"
  | "notFound"
  | "limit"
  | "conflict"
  | "validation"
  | "error";

export interface UiState {
  kind: UiStateKind;
  title: string;
  description: string;
  /** Yeniden deneme SUNULABİLİR mi (aşağıdaki kurala bakınız). */
  retry: boolean;
  /** `RATE_LIMITED` gibi durumlarda sunucunun verdiği bekleme süresi. */
  retryAfterSeconds?: number;
}

/**
 * Sunucunun metni GÜVENLİ mi?
 *
 * `INTERNAL` ve `PROVIDER_ERROR` mesajları altyapı adı, bağlantı dizesi ya da yığın izi
 * taşıyabilir — sunucu bunları maskeliyor ama istemci ona körlemesine güvenmemeli
 * (savunmanın ikinci katmanı). Diğer kodların metinleri kullanıcı için YAZILMIŞ
 * metinlerdir ve olduğu gibi gösterilir (refleks 4).
 */
/** `ApiRequestError.code` `string` tipli (zarf sürüm atlarsa bilinmeyen kod gelebilir). */
const GUVENSIZ_METIN: readonly string[] = [ERROR_CODES.INTERNAL, ERROR_CODES.PROVIDER_ERROR];

const GENEL_HATA = "İşlem tamamlanamadı. Lütfen tekrar deneyin.";

export interface ResolveOptions {
  /**
   * İşlem AYNI SONUCU vererek tekrarlanabilir mi?
   *
   * ⚠ Varsayılan `false` — FAIL-SAFE. Kartın runtime koşulu: *"yeniden deneme idempotent
   * olmayan komutu kullanıcı onayı olmadan körlemesine tekrarlamaz."* Bir sözleşmeyi
   * imzalayan isteği "yeniden dene" düğmesiyle tekrarlamak, ikinci bir imza atmak
   * olabilirdi. Okuma yolları bunu açıkça `true` yapar.
   */
  idempotent?: boolean;
  /**
   * Kaydın VARLIĞI sır mı?
   *
   * ⚠ Varsayılan `true`. Kartın ikinci kriteri: *"erişim yok ile bulunamadı, KAYIT
   * VARLIĞINI SIZDIRMAMA ihtiyacına göre güvenli biçimde ayrılır veya birleştirilir."*
   * Birleşik mesaj, elinde kimlik olan birinin "bu kayıt var ama göremiyorum" bilgisini
   * toplamasını engelliyor. Varlığın sır olmadığı yüzeylerde (kendi listesinden gelen
   * kayıt) çağıran `false` diyerek daha yardımcı bir metin alabilir.
   */
  existenceIsSecret?: boolean;
  /**
   * Belirli bir durumun metnini domainin daha DOĞRU metniyle değiştirir.
   *
   * Örnek: bir Üst Bar panelinde 403, "kayda erişemiyorsun" değil "bu ÇALIŞMA ALANINA
   * erişimin değişti" demektir. Genel metni dayatmak bir gerileme olurdu; domainin kendi
   * `catch` bloğunda ham `error.message` basmasına izin vermek ise kartın ilk kriterini
   * çiğnerdi. Bu kanca ikisinin arasından geçiyor: metin domainin, GÜVENLİK KAPISI ortak.
   */
  overrides?: Partial<Record<UiStateKind, { title: string; description: string }>>;
}

/** Tarayıcı çevrimdışıysa bunu söylemek, ham bir hata kodundan daha yardımcı. */
function cevrimdisi(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function resolveErrorState(error: unknown, options: ResolveOptions = {}): UiState {
  const durum = cozumle(error, options);
  const ozel = options.overrides?.[durum.kind];
  return ozel ? { ...durum, ...ozel } : durum;
}

function cozumle(error: unknown, options: ResolveOptions): UiState {
  const { idempotent = false, existenceIsSecret = true } = options;

  /**
   * ⚠ ÇEVRİMDIŞI DALI YALNIZ SUNUCU CEVABI YOKKEN.
   *
   * `ApiRequestError` ancak `fetch` başarılı olup sunucudan bir zarf döndüğünde
   * fırlıyor (`apiClient.ts`); ağ gerçekten koptuğunda `fetch` reddeder ve buraya
   * BAŞKA bir hata gelir. Yani elde bir `ApiRequestError` varsa sunucuya ulaşılmış
   * demektir ve `navigator.onLine` o an ne derse desin yanılıyordur.
   *
   * Eskiden bu dal en başta ve koşulsuzdu. Ölçüldü: `navigator.onLine` yanlışlıkla
   * `false` takılı kaldığında (VPN/proxy geçişi, uykudan dönüş, otomasyon oturumu)
   * ürünün TÜM hata ekranları "İnternet bağlantınız kesildi" diyordu — silinmiş bir
   * sözleşme, yetkisiz erişim, hatta doğrulama hatası bile. Kullanıcı gerçek sebebi
   * hiç görmüyor, modemine bakmaya gidiyordu.
   */
  if (!(error instanceof ApiRequestError)) {
    if (cevrimdisi()) {
      return {
        kind: "offline",
        title: "Bağlantı yok",
        description: "İnternet bağlantınız kesildi. Bağlantı döndüğünde tekrar deneyin.",
        retry: idempotent,
      };
    }
    return { kind: "error", title: "Bir sorun oluştu", description: GENEL_HATA, retry: idempotent };
  }

  /**
   * ⚠ İSTEMCİNİN KENDİ ÜRETTİĞİ ÇEVRİMDIŞI HATASI (CLOS-1278).
   *
   * Üstteki dal *"elde `ApiRequestError` varsa sunucuya ulaşılmıştır"* diyor ve haklı —
   * ama `apiClient`'ın çevrimdışı kapısı, ağa hiç çıkmadan bu tipte bir hata fırlatıyor.
   * Kod `OFFLINE` olduğunda sunucuya ulaşılmadığı KESİN; bu yüzden ayrı ve ÖNCE.
   *
   * ⚠ `retry` burada koşulsuz `true`: istek ağa çıkmadığı için sunucuda yarım kalmış bir
   * işlem yok. Yazma yolunda bile tekrarlamak güvenli — `idempotent` süzgecine bağlasaydık
   * kullanıcı, aslında hiç olmamış bir onayı tekrar denemekten alıkonurdu.
   */
  if (error.code === "OFFLINE") {
    return {
      kind: "offline",
      title: "Bağlantı yok",
      description: error.message,
      retry: true,
    };
  }

  const sunucuMetni = GUVENSIZ_METIN.includes(error.code) ? GENEL_HATA : error.message;
  /**
   * ⚠ Alan mesajı GÜVENSİZ METİN listesine tabi: kimlik/yetki hatalarında alan ayrıntısı da
   * sızdırmamalı. Liste dışında kalan doğrulama hatalarında ise asıl açıklama burada.
   */
  const alanMetni = GUVENSIZ_METIN.includes(error.code)
    ? null
    : (error.fields?.find((a) => a.message.trim().length > 0)?.message ?? null);
  /**
   * YENİDEN DENEME KARARI (CLOS-893 [S03] açık kararı).
   *
   * ⚠ Kural artık İŞLEMİN CİNSİ: okuma (idempotent) yolunda sunuluyor, yazma yolunda
   * sunulmuyor. Kataloğun `retryable` alanı burada SÜZGEÇ DEĞİL.
   *
   * Eskiden "katalog izin veriyorsa VE idempotentse" idi. `INTERNAL` katalogda
   * `retryable: false` olduğu için sunucu 500 verdiğinde OKUMA ekranı çıkmaz sokağa
   * dönüyordu: kullanıcının tek yolu sayfayı/paneli kapatıp yeniden açmaktı. Bir GET'i
   * tekrarlamak her zaman güvenli; kataloğun "tekrar denenemez" demesi "işe yaramaz"
   * anlamına gelir, "zararlı" değil.
   *
   * ⚠ Kataloğu doğrudan VEYA'lamak YANLIŞ olurdu: `RATE_LIMITED` katalogda
   * `retryable: true` ve o hâlde hız sınırına takılan bir POST'a da yeniden deneme
   * sunulurdu — kartın açıkça yasakladığı şey (*"idempotent olmayan komutu kullanıcı
   * onayı olmadan körlemesine tekrarlamaz"*). Yazma tarafı bu yüzden AYNEN duruyor.
   *
   * ⚠ 403, 404, çakışma ve doğrulama dalları kendi `retry: false` kararlarını taşıyor;
   * bu değişiklik onlara dokunmuyor — yalnız limit ve genel hata dalları etkileniyor.
   */
  const denenebilir = idempotent;

  switch (error.code) {
    case ERROR_CODES.UNAUTHORIZED:
      return {
        kind: "unauthorized",
        title: "Oturumunuz doğrulanamadı",
        description: "Devam etmek için yeniden giriş yapın.",
        retry: false,
      };

    /**
     * ⚠ `FORBIDDEN`'dan AYRI ele alınıyor (CLOS-292 `[046]`). "Yetkin yok" bir duvar,
     * "2FA kur" bir adım: ikisini aynı ekrana koymak kullanıcıya yapabileceği bir şey
     * olduğunu söylemezdi. Metin sunucudan geliyor ve kullanıcı için yazılmış.
     */
    case ERROR_CODES.TWO_FACTOR_REQUIRED:
      return {
        kind: "twoFactorRequired",
        title: "İki aşamalı doğrulama gerekiyor",
        description: sunucuMetni,
        retry: false,
      };

    /**
     * ⚠ 403 BİRLEŞTİRİLMİYOR — sunucu o kararı ZATEN VERDİ (BUG-006 · Mega E2E).
     *
     * Burası eskiden 403'ü de "Kayıt bulunamadı"ya düşürüyordu; gerekçe kayıt varlığını
     * sızdırmamaktı. Ama bu depoda varlığı gizleme kararı SUNUCUDA alınıyor ve orada 404
     * dönülüyor — `middleware/requireRecordAccess.ts`: *"Cevap 404, 403 DEĞİL … erişimi
     * olmayan için kayıt YOKTUR"*; `lib/recordAccess/service.ts`: *"üç farklı sebep aynı
     * cevabı veriyor: notFound"*.
     *
     * Yani istemciye bir 403 ULAŞTIYSA, sunucu varlığın sır olmadığına çoktan karar
     * vermiştir. İstemcide ikinci kez birleştirmek hiçbir şeyi korumuyor, yalnız sunucunun
     * bilerek sakladığı ayrımı yok ediyordu: yetkisi yetmediği için başarısız olan
     * kullanıcı "kayıt bulunamadı" okuyor ve yanlış yerde çözüm arıyordu.
     *
     * ⚠ Birleştirme KALKMADI, doğru yerine oturdu: 404'te sürüyor — sunucunun iki sebebi
     * birleştirdiği yer tam orası.
     */
    case ERROR_CODES.FORBIDDEN:
      return {
        kind: "forbidden",
        title: "Bu işlem için yetkiniz yok",
        // ⚠ HANGİ yetkinin eksik olduğu SÖYLENMİYOR: permission ayrıntısı, saldırgana
        // yetki haritası çıkarır (kartın ilk kriteri).
        description: "Erişim için organizasyon yöneticinizle görüşün.",
        retry: false,
      };

    case ERROR_CODES.NOT_FOUND:
      return existenceIsSecret
        ? {
            kind: "forbiddenOrMissing",
            title: "Kayıt bulunamadı",
            description: "Kayıt silinmiş olabilir ya da görüntüleme yetkiniz olmayabilir.",
            retry: false,
          }
        : {
            kind: "notFound",
            title: "Kayıt bulunamadı",
            description: "Aradığınız kayıt silinmiş ya da taşınmış olabilir.",
            retry: false,
          };

    case ERROR_CODES.QUOTA_EXCEEDED:
    case ERROR_CODES.RATE_LIMITED:
      return {
        kind: "limit",
        title: "Sınıra ulaşıldı",
        // Metin merkezî motordan geliyor; burada YENİDEN HESAPLANMIYOR (kart kriteri 6).
        description: sunucuMetni,
        retry: denenebilir,
        retryAfterSeconds: error.retry?.afterSeconds,
      };

    case ERROR_CODES.CONFLICT:
    case ERROR_CODES.CONCURRENCY_CONFLICT:
    case ERROR_CODES.IDEMPOTENCY_CONFLICT:
    case ERROR_CODES.IDEMPOTENCY_IN_PROGRESS:
      return {
        kind: "conflict",
        title: "İşlem çakıştı",
        description: sunucuMetni,
        /**
         * ⚠ Çakışmada yeniden deneme SUNULMUYOR. `CONCURRENCY_CONFLICT` başkasının
         * değişikliğinin üstüne yazılmak üzere olduğu anlamına gelir; körlemesine tekrar
         * o değişikliği ezerdi (`apps/web/CLAUDE.md`: kaydı yeniden oku).
         */
        retry: false,
      };

    /**
     * ⚠ ALAN METNİ ZARF METNİNİN ÖNÜNDE (BUG-015 · Mega E2E'de ölçüldü).
     *
     * Sunucu şema hatasında zarfa GENEL bir metin yazıyor ("Girilen bilgiler geçersiz") ve
     * asıl açıklamayı `fields[].message`'a koyuyor — izin verilmeyen dosya türünde
     * "Bu dosya türü desteklenmiyor" gibi. Formu olan yüzeyler bunu `applyServerErrors` ile
     * alana yazıyor; formu OLMAYAN yüzeyler (dosya yükleme) yalnız zarf metnini gösteriyor
     * ve kullanıcı reddin sebebini hiç görmüyordu.
     *
     * ⚠ Sunucu zaten doğru davranıyordu (`CONTRACT_FILE_REJECTION_NAMES` her sebep için
     * ayrı, yardımcı bir metin taşıyor). Kayıp tamamen istemcideydi.
     *
     * ⚠ İLK alan mesajı seçiliyor, hepsi birleştirilmiyor: birden çok alan hatası olan bir
     * formda o zaten alan alan gösteriliyor; burada amaç formu olmayan yüzeye TEK ve
     * anlaşılır bir cümle vermek.
     */
    case ERROR_CODES.VALIDATION:
    case ERROR_CODES.BAD_REQUEST:
    case ERROR_CODES.PAYLOAD_TOO_LARGE:
      return {
        kind: "validation",
        title: "Gönderilen bilgiler kabul edilmedi",
        description: alanMetni ?? sunucuMetni,
        retry: false,
      };

    default:
      return {
        kind: "error",
        title: "Bir sorun oluştu",
        description: sunucuMetni,
        retry: denenebilir,
      };
  }
}
